import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore, isTokenExpiringSoon } from '../../store';
import { resolveApiBaseUrl } from '../config/apiBaseUrl';

const API_BASE_URL = resolveApiBaseUrl();

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Track if a token refresh is in progress
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Subscribe to token refresh
function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

// Notify all subscribers with new token
function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

// Request interceptor: Attach access token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { tokens, refreshTokens } = useAuthStore.getState();

    if (tokens?.accessToken) {
      // Check if token is expiring soon and proactively refresh
      if (isTokenExpiringSoon(tokens.accessToken) && !isRefreshing) {
        isRefreshing = true;
        try {
          const success = await refreshTokens();
          if (success) {
            const newTokens = useAuthStore.getState().tokens;
            if (newTokens?.accessToken) {
              config.headers.Authorization = `Bearer ${newTokens.accessToken}`;
            }
          }
        } finally {
          isRefreshing = false;
        }
      } else {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // If error is not 401 or request already retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't retry auth endpoints
    if (originalRequest.url?.includes('/api/auth/')) {
      return Promise.reject(error);
    }

    // If refresh is already in progress, wait for it
    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((token: string) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(apiClient(originalRequest));
        });
      });
    }

    // Start refresh process
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { refreshTokens, logout } = useAuthStore.getState();
      const success = await refreshTokens();

      if (success) {
        const newTokens = useAuthStore.getState().tokens;
        if (newTokens?.accessToken) {
          // Notify all waiting requests
          onTokenRefreshed(newTokens.accessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return apiClient(originalRequest);
        }
      }

      // Refresh failed - logout and reject
      await logout();
      // Redirect to login (will be handled by React Router)
      window.location.href = '/login';
      return Promise.reject(error);
    } catch (refreshError) {
      // Refresh threw error - logout and reject
      const { logout } = useAuthStore.getState();
      await logout();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// Legacy export for backwards compatibility
export const axiosInstance = apiClient;

export default apiClient;
